import { Flex, Center } from '@chakra-ui/react';
import { shortDate } from '@/pages/reports/non-ats-reports/shared/utils/date-group';
import dayjs from 'dayjs';
import { CustomDate } from '../custom-date-range';
import { colors } from '@/theme/foundations/colors';
import { KText } from '@/ui-library/components/text';
import { useKFlags } from '@/hooks/use-k-flags';

const getCompareTime = (timeFrame) => {
  const { from, to } = timeFrame;
  if (!to) {
    const noOfDays = shortDate[from];
    const toDate = dayjs().subtract(1, 'day');
    const fromDate = dayjs(toDate).subtract(noOfDays - 1, 'day');
    const formattedCompareFrom = fromDate.format('DD MMM YY');
    const formattedCompareTo = toDate.format('DD MMM YY');
    return { from: formattedCompareFrom, to: formattedCompareTo };
  }
};

const SelectedTime = ({ selected, id, color, size }) => {
  const { feReportsV3 } = useKFlags();
  const display = getCompareTime(selected);
  if (selected?.from !== id) return null;
  if (id === 'past1d') {
    return <> ({display?.from})</>;
  }
  return (
    <KText
      color={color}
      size={size}
      {...(feReportsV3 && { fontWeight: '550px' })}
    >
      {' '}
      ({display?.from} - {display?.to})
    </KText>
  );
};

const Timeframe = ({
  isEditMode = false,
  label = 'Today',
  id = 'today',
  selected = {},
  onChange = () => {},
}) => {
  const { feReportsV3 } = useKFlags();
  const bg =
    selected?.from === id
      ? feReportsV3
        ? 'primary-100'
        : 'primary-500'
      : 'base-white';
  const color =
    selected?.from === id
      ? feReportsV3
        ? 'primary-600'
        : 'base-white'
      : 'secondary-700';
  const size =
    selected?.from === id
      ? feReportsV3
        ? 'sm-regular'
        : 'sm-semi-bold'
      : 'sm-regular';

  return (
    <Center
      pointerEvents={selected?.from === id ? 'none' : 'unset'}
      className="timeframe-elm"
      height={feReportsV3 ? '32px' : '38px'}
      px="12px"
      fontWeight={
        selected?.from === id
          ? feReportsV3
            ? 550
            : 700
          : feReportsV3
          ? 550
          : 700
      }
      fontSize="14px"
      _hover={{ bg: 'secondary-25' }}
      cursor="pointer"
      bg={bg}
      color={color}
      onClick={onChange}
      data-test-id={`tab-timeframe-${id}`}
    >
      <Flex gap="4px">
        <KText
          size={size}
          color={color}
          {...(feReportsV3 && { fontWeight: '550px' })}
        >
          {label}
        </KText>
        {!isEditMode && (
          <SelectedTime selected={selected} id={id} color={color} size={size} />
        )}{' '}
      </Flex>
    </Center>
  );
};

export const TimeframeView = ({
  isEditMode = false,
  selected = {},
  onChange = () => {},
  onChangeCustomTime = () => {},
}) => {
  const { feReportsV3 } = useKFlags();

  const onChangeTimeFrame = (selected) => {
    onChange(selected);
  };

  return (
    <Flex
      border="1px solid"
      borderColor="border-color"
      height={feReportsV3 ? '32px' : '40px'}
      alignItems="center"
      borderRadius="8px"
      overflow="hidden"
      data-test-id="section-timeframe-filter"
      css={`
        > .timeframe-elm {
          border-right: 1px solid;
          border-color: ${colors['border-color']};
        }
        > .timeframe-elm:first-of-type {
          border-radius: 8px 0 0 8px;
        }
      `}
    >
      <Timeframe
        isEditMode={isEditMode}
        label="Yesterday"
        id="past1d"
        selected={selected}
        onChange={() => {
          onChangeTimeFrame({ from: 'past1d' });
        }}
      />
      <Timeframe
        isEditMode={isEditMode}
        label="7D"
        id="past7d"
        selected={selected}
        onChange={() => {
          onChangeTimeFrame({ from: 'past7d' });
        }}
      />
      <Timeframe
        isEditMode={isEditMode}
        label="30D"
        id="past30d"
        selected={selected}
        onChange={() => {
          onChangeTimeFrame({ from: 'past30d' });
        }}
      />
      <Timeframe
        isEditMode={isEditMode}
        label="3M"
        id="past3m"
        selected={selected}
        onChange={() => {
          onChangeTimeFrame({ from: 'past3m' });
        }}
      />
      <Timeframe
        isEditMode={isEditMode}
        label="12M"
        id="past12m"
        selected={selected}
        onChange={() => {
          onChangeTimeFrame({ from: 'past12m' });
        }}
      />
      <CustomDate onChange={onChangeCustomTime} selected={selected} />
    </Flex>
  );
};
